/**
 * @function makeValidator
 *
 * @argument function validate - A function returning a value for errorMessage, or true, given a (coerced and normalized, if provided) value.
 * @argument [object] options
 * @argument [function] options.normalize - Clean a value before validating or committing.
 * @argument [function] options.coerce - Coerce a given value on input.
 * @argument [function] options.isNull - Determine whether defaultValue should be used in place of value on mount.
 * @argument [function|*] defaultValue - A default value to use instead of the given value on mount if the given value is `NaN`, `undefined`, `null` or `""`. Manually when this is used by setting the `isNull` function in options. If a function is provided it will be called with `options`.
 *
 * @returns Validator
 *
 * @description
 *  `makeValidator` creates a class that takes `value` and an optional `onChange` handler. A local state is created and all changes are kept, but `onChange` is only called with states that pass validation. `onChange`, `onRevert`, `value`, `valid`, `error`, and `errorMessage` are provided stored on the object.
 *
 */

export default (
  validate,
  { normalize, coerce, isNull = value => [undefined, null, '', NaN].includes(value) } = {},
  defaultValue
) => {
  const getValidation = value => {
    if (validate instanceof Function) {
      const result = validate(value);
      return {
        error: result !== true ? true : undefined,
        valid: result === true,
        errorMessage: result !== true ? result : undefined
      };
    }
    return { error: false, valid: true, errorMessage: null };
  };
  const getDefaultOrValue = (value, options) =>
    defaultValue !== undefined && isNull instanceof Function && isNull(value)
      ? defaultValue instanceof Function
        ? defaultValue(options)
        : defaultValue
      : value;
  const getNormalValue = value => (normalize instanceof Function ? normalize(value) : value);
  const getCoercedValue = value => (coerce instanceof Function ? coerce(value) : value);
  const validationState = value => {
    const coercedValue = getCoercedValue(value);
    const normalValue = getNormalValue(coercedValue);
    const validation = getValidation(normalValue);
    return {
      ...validation,
      normalValue,
      value: coercedValue
    };
  };

  // This was modified from it's original form as a Higher-Order React Component.
  return class Validator {
    constructor(options = {}) {
      const validation = validationState(getDefaultOrValue(options.value, options));
      Object.assign(this, options, validation);
      this.state = validation;
      this.options = options;
    }

    static create(options) {
      return new Validator(options);
    }

    update(value) {
      const { onChange, onError } = this;
      const validation = validationState(value);
      if (validation.valid && onChange instanceof Function) {
        onChange(validation.normalValue, validation.value);
      }
      if (validation.error && onError instanceof Function) {
        onError(validation.errorMessage, value);
      }
      // Local value shouls always be valid.
      if (validation.valid) {
        Object.assign(this, validation);
      }
      this.state = validation;
      return this;
    }

    clear() {
      this.update(undefined);
    }

    revert() {
      this.update(this.value);
      if (onRevert instanceof Function) onRevert(this.state.normalValue, this.state.value);
      return this;
    }
  };
};
